import { ShaderModule, StorageSource, LambdaSource, UniformAttribute, DataBinding, ModuleRef, RefFlags as RF, VirtualRender } from './types';

import { formatMurmur53, toMurmur53, getObjectKey, mixBits, scrambleBits } from '../util/hash';
import { getBundleHash, getBundleEntry, getBundleName, toBundle, toModule } from '../util/bundle';
import { getBindingArgument } from '../util/bind';
import { loadVirtualModule, bundleToAttribute } from './shader';
import { castTo, makeSwizzle } from './operators/cast';
import { PREFIX_VIRTUAL } from '../constants';
import { VIRTUAL_BINDGROUP, VOLATILE_BINDGROUP } from './constants';

const INT_PARAMS = [{name: 'index', type: {name: 'u32'}}];
const INT_ARG = ['u32'];
const UV_ARG = ['vec2<f32>'];

const arg = (x: number) => String.fromCharCode(97 + x);

const is3to4 = (type: string) => type.match(/vec3to4</);
const to3 = (type: string) => type.replace(/vec3to4</, 'vec3<');
const to1 = (type: string) => type.replace(/vec[^<]+<([^>]+)>/, '$1');

const is8to32 = (type: string) => type.match(/^(u|i)8$/);
const is16to32 = (type: string) => type.match(/^(u|i)16$/);
const isVec8to32 = (type: string) => type.match(/^vec[234]<(u|i)8>$/);
const isVec16to32 = (type: string) => type.match(/^vec[234]<(u|i)16>$/);

const to32 = (type: string) => type.replace(/^(vec[234]<)?([ui])[0-9]+/, '$1$232');

const needsCast = (from: string, to: string) => {
  if (from.match(/[A-Z]/)) return false;
  return from.replace(/[^0-9]+/, '') != to.replace(/[^0-9]+/, '');
};

const getTypeKey = (b: DataBinding) =>
  (+!!b.constant) +
  (+!!b.storage) * 2 +
  (+!!b.lambda) * 4 +
  (+!!b.texture) * 8 +
  (+!!b.uniform) * 16 +
  (+!!(b.uniform?.volatile || b.storage?.volatile || b.texture?.volatile)) * 32;

const getFormatKey = (b: DataBinding) =>
  b.texture ? toMurmur53(b.texture?.format) ^
              toMurmur53(b.texture?.layout) ^
              toMurmur53(b.texture?.variant) ^
              toMurmur53(b.texture?.absolute) :
  b.storage ? toMurmur53(b.storage?.format) :
  b.uniform ? toMurmur53(b.uniform?.format) :
  0;

const getBindingsKey = (bs: DataBinding[]) => scrambleBits(bs.reduce((a, b) => mixBits(a, getTypeKey(b) ^ getFormatKey(b)), 0)) >>> 0;
const getValueKey = (b: DataBinding) => getObjectKey(b.constant ?? b.uniform ?? b.storage ?? b.texture);

const resolveType = (
  rename: Map<string, string>,
  format: string,
  type?: ShaderModule,
) => {
  if (!type) return format;

  const entry = getBundleEntry(type);
  const t = entry != null ? rename.get(entry) ?? entry ?? null : null;
  if (t == null) throw new Error(`Invalid type '${getBundleName(type)}'. Module has no entry point.`);

  return t;
};

export const makeBindingAccessors = (
  bindings: DataBinding[],
): Record<string, ShaderModule> => {

  // Extract attributes by type
  const lambdas = bindings.filter(({lambda}) => lambda != null);
  const uniforms = bindings.filter(({uniform}) => uniform != null);
  const samplers = bindings.filter(({sampler}) => sampler != null);
  const storages = bindings.filter(({storage}) => storage != null);
  const textures = bindings.filter(({texture}) => texture != null);
  const constants = bindings.filter(({constant}) => constant != null);

  const buffers = [...uniforms, ...storages];
  const structs = bindings.filter(({attribute, constant, storage, uniform}) =>
    !!attribute.type && (constant || storage || uniform));

  // Virtual module symbols
  const virtuals = [...constants, ...uniforms, ...samplers, ...storages, ...textures];
  const symbols = virtuals.map(({attribute}) => attribute.name);
  const types = virtuals.map(({attribute}) => attribute.format);
  const exports = virtuals.map((binding) => ({
    func: {
      name: binding.attribute.name,
      type: {name: resolveBindingValueType(binding)},
      parameters: binding.attribute.args ?? INT_PARAMS,
    },
    flags: RF.Exported,
  })) as any[];

  // Inject import for constant/storage struct types
  const libs: Record<string, ShaderModule> = {};
  const modules = structs.map(({attribute, constant, storage, uniform}) => {
    const {type: typeOut} = attribute;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const {type: typeIn} = (storage ?? uniform ?? constant)!;

    const type = typeOut ?? typeIn;
    if (type) {
      const entry = getBundleEntry(type);
      if (entry != null) {
        const module = toModule(type);
        libs[module.name] = module;
        return {
          at: 0,
          name: module.name,
          imports: [{name: entry, imported: entry}],
          symbols: [entry],
        } as ModuleRef;
      }
    }

    return null;
  }).filter((m: any) => !!m) as ModuleRef[];

  // Hash + readable representation
  const readable = symbols.join(' ');
  const signature = getBindingsKey(bindings).toString(16);
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const external = lambdas.map(l => getBundleHash(l.lambda!.shader));
  const unique = `@access [${signature}] [${external}] [${readable}] [${types.join(' ')}]`;

  const hash = toMurmur53(unique);
  const code = `@access [${readable}] [${formatMurmur53(hash)}]`;

  const keyed = bindings.reduce((a, s) => mixBits(a, getValueKey(s)), 0);
  const key   = toMurmur53(`${formatMurmur53(hash)} ${keyed}`);

  // Code generator
  const render = (
    namespace: string,
    rename: Map<string, string>,
    bindingBase: number = 0,
    volatileBase: number = 0,
  ) => {
    const program: string[] = [];
    const bindingSet = getBindingArgument(rename.get(VIRTUAL_BINDGROUP));
    const volatileSet = getBindingArgument(rename.get(VOLATILE_BINDGROUP));

    for (const binding of constants) {
      const {attribute: {name, format, type, args}} = binding;

      const t = resolveType(rename, format, type);
      program.push(makeUniformFieldAccessor(PREFIX_VIRTUAL, namespace, t, name, args as any));
    }

    for (const binding of samplers) {
      const {attribute: {name, format}} = binding;

      const set = bindingSet;
      const base = bindingBase++;

      program.push(makeSamplerAccessor(namespace, set, base, format, name));
    }

    for (const binding of buffers) {
      const {attribute: {name, format: formatOut, type: typeOut, args}, storage, uniform} = binding;

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const {volatile, format: formatIn, type: typeIn, readWrite} = (storage ?? uniform)! as StorageSource;
      const set = volatile ? volatileSet : bindingSet;
      const base = volatile ? volatileBase++ : bindingBase++;

      const type = typeOut ?? typeIn;
      if (type) {
        const format = type === typeOut ? formatOut : formatIn;
        let t = resolveType(rename, format, type);

        if (format === 'array<T>') t = `array<${t}>`;
        program.push(makeStorageAccessor(namespace, set, base, t, t, name, readWrite, !!uniform, args));
        continue;
      }

      if (Array.isArray(formatIn) || Array.isArray(formatOut)) throw new Error(`Cannot bind data to a type`);

      if (formatIn === 'T' || formatOut === 'T') {
        // eslint-disable-next-line no-debugger
        debugger;
      }

      if (is3to4(formatIn)) {
        const accessor = name + '3to4';
        program.push(makeStorageAccessor(namespace, set, base, to1(formatIn), to1(formatIn), accessor, readWrite, !!uniform));
        program.push(makeVec3to4Accessor(namespace, formatOut, to3(formatIn), name, accessor));
        continue;
      }
      else if (is8to32(formatIn)) {
        const accessor = name + '8to32';
        program.push(makeStorageAccessor(namespace, set, base, 'u32', 'u32', accessor, readWrite, !!uniform));
        program.push(make8to32Accessor(namespace, formatOut, to32(formatIn), name, accessor));
        continue;
      }
      else if (is16to32(formatIn)) {
        const accessor = name + '16to32';
        program.push(makeStorageAccessor(namespace, set, base, 'u32', 'u32', accessor, readWrite, !!uniform));
        program.push(make16to32Accessor(namespace, formatOut, to32(formatIn), name, accessor));
        continue;
      }
      else if (isVec8to32(formatIn)) {
        const accessor = name + 'Vec8to32';
        const wide = to32(formatIn).replace('i32', 'u32');
        program.push(makeStorageAccessor(namespace, set, base, 'u32', 'u32', accessor, readWrite, !!uniform));
        program.push(makeVec8to32Accessor(namespace, formatOut, wide, name, accessor));
        continue;
      }
      else if (isVec16to32(formatIn)) {
        const accessor = name + 'Vec16to32';
        const wide = to32(formatIn).replace('i32', 'u32');
        program.push(makeStorageAccessor(namespace, set, base, 'u32', 'u32', accessor, readWrite, !!uniform));
        program.push(makeVec16to32Accessor(namespace, formatOut, wide, name, accessor));
        continue;
      }

      program.push(makeStorageAccessor(namespace, set, base, formatOut, formatIn, name, readWrite, !!uniform, args));
    }

    for (const binding of textures) {
      const {attribute: {name, format: formatOut, args}, texture} = binding;

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const {volatile, layout, variant, absolute, sampler, filter, format: formatIn, aspect} = texture!;
      const set = volatile ? volatileSet : bindingSet;
      const base = volatile ? volatileBase++ : bindingBase++;
      if (sampler && args !== null) volatile ? volatileBase++ : bindingBase++;

      const type = formatOut.match(/auto(<|$)/) ? resolveBindingValueType(binding) : formatOut as string;
      program.push(makeTextureAccessor(namespace, set, base, type, formatIn, name, layout, variant, aspect, absolute, !!sampler, filter, args));
    }

    return program.join('\n');
  }

  const virtual = loadVirtualModule({
    constants,
    uniforms,
    samplers,
    storages,
    textures,
    render,
  }, {
    symbols,
    exports,
    modules: modules.length ? modules : undefined,
  }, undefined, hash, code, key);

  const bundle = Object.keys(libs).length ? {
    module: virtual,
    libs,
  } : virtual;

  const links: Record<string, ShaderModule> = {};
  for (const {attribute} of constants) links[attribute.name] = bundle;
  for (const {attribute} of uniforms)  links[attribute.name] = bundle;
  for (const {attribute} of samplers)  links[attribute.name] = bundle;
  for (const {attribute} of storages)  links[attribute.name] = bundle;
  for (const {attribute} of textures)  links[attribute.name] = bundle;
  for (const {attribute, lambda} of lambdas)   {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const needsCast = needsCastLambdaType(attribute, lambda!);
    links[attribute.name] = needsCast
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ? castTo(lambda!.shader, attribute.format as string)
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      : lambda!.shader;
  }

  return links;
};

export const resolveBindingValueType = (binding: DataBinding) => {
  const {attribute: {name, format}} = binding;
  if (!format.match(/auto(<|$)/)) return format;

  const {uniform, sampler, storage, texture} = binding;
  if (uniform) return uniform.format;
  if (sampler) return 'sampler';
  if (storage) return storage.format;
  if (texture) {
    if (texture.format.match(/depth/)) return 'f32';
    if (texture.layout.match(/<u32>/)) return 'vec4<u32>';
    if (texture.layout.match(/<i32>/)) return 'vec4<i32>';
    return 'vec4<f32>';
  }

  throw new Error(`Binding '${name}' with 'auto' format could not be inferred`);
};

export const needsCastLambdaType = (
  attribute: UniformAttribute,
  lambda: LambdaSource,
) => {
  const {name, format: from} = attribute;

  const bundle = toBundle(lambda.shader);
  const lambdaAttribute = bundleToAttribute(bundle);
  const {format: to} = lambdaAttribute;

  if (Array.isArray(from) || Array.isArray(to)) return false;

  let f = from;
  let t = to;

  if (f === t) return false;
  if (f.match(/auto(<|$)/) || t.match(/auto(<|$)/)) return false;
  if (t == null) {
    console.warn(`Unable to determine lambda format for attribute ${attribute.name} -> bundle ${getBundleEntry(bundle)}`)
    return false;
  }

  // Remove vec<..> to allow for automatic widening/narrowing
  f = f.replace(/vec[0-9]/, '').replace(/^<|>$/g, '');
  t = t.replace(/vec[0-9]/, '').replace(/^<|>$/g, '');

  // Shorthand
  if (f.match(/^vec[0-9]uif$/)) f += '32';
  if (f.match(/^vec[0-9]h$/))   f = 'f16';
  if (t.match(/^vec[0-9]uif$/)) t += '32';
  if (t.match(/^vec[0-9]h$/))   t = 'f16';

  if (f !== t) {
    // Remove bit size to allow for automatic widening/narrowing
    const fromScalar = f.replace(/([uif])([0-9]+)/, '$1__');
    const toScalar   = t.replace(/([uif])([0-9]+)/, '$1__');

    if (fromScalar !== toScalar) {
      // uppercase = struct type, allow any
      if (from.match(/[A-Z]/) && to) return false;

      throw new Error(`Invalid format ${to} bound for ${from} "${name}" (${fromScalar} != ${toScalar})`);
    }

    return true;
  }

  return true;
};

export const makeUniformRender = (
  constants: DataBinding[],
  set: number | string = 0,
  binding: number | string = 0,
): VirtualRender => (
  namespace: string,
  rename: Map<string, string>,
) => {
  // Uniform Buffer Object struct members
  const members = constants.map(({attribute: {name, type, format}}) => {
    const t = resolveType(rename, format, type);
    return `${name}: ${t}`;
  });

  return members.length ? makeUniformBlockLayout(PREFIX_VIRTUAL, set, binding, members) : '';
};

export const makeUniformBlockLayout = (
  ns: string,
  set: number | string,
  binding: number | string,
  members: string[],
) => (
`struct ${ns}Type {
  ${members.map(m => `${m},`).join('\n  ')}
};
@group(${set}) @binding(${binding}) var<uniform> ${ns}Uniform: ${ns}Type;
`);

export const makeUniformFieldAccessor = (
  uniform: string,
  ns: string,
  type: string,
  name: string,
  args: string[] | null = INT_ARG,
) => {
  if (args == null) throw new Error("Constants cannot be bound directly to storage/textures");
  return (
`fn ${ns}${name}(${args.map((t, i) => `${arg(i)}: ${t}`).join(', ')}) -> ${type} {
  return ${uniform}Uniform.${ns}${name};
}
`);
};

export const makeStorageAccessor = (
  ns: string,
  set: number | string,
  binding: number | string,
  type: string,
  format: string,
  name: string,
  readWrite?: boolean,
  uniform?: boolean,
  args: string[] | null = INT_ARG,
) => {
  const access = uniform ? 'uniform' : readWrite ? 'storage, read_write' : 'storage';

  if (args === null) {
    return `@group(${set}) @binding(${binding}) var<${access}> ${ns}${name}: ${type};\n`;
  }

  const hasCast = needsCast(format, type);
  return (
`@group(${set}) @binding(${binding}) var<${access}> ${ns}${name}Storage: array<${format}>;

fn ${ns}${name}(${args.map((t, i) => `${arg(i)}: ${t}`).join(', ')}) -> ${type} {
  ${hasCast ? 'let v =' : 'return'} ${ns}${name}Storage[${args.length ? arg(0) : '0u'}];
${hasCast ? `  return ${makeSwizzle(format, type, 'v')};\n` : ''
}}
`);
}

export const makeTextureAccessor = (
  ns: string,
  set: number | string,
  binding: number,
  type: string,
  format: string,
  name: string,
  layout: string,
  variant: string = 'textureSample',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  aspect: string = 'all',
  absolute: boolean = false,
  sampler: boolean = true,
  filter: 'filtering' | 'non-filtering' | 'comparison' = 'filtering',
  args: string[] | null = UV_ARG,
) => {
  if (args === null) {
    return `@group(${set}) @binding(${binding}) var ${ns}${name}: ${type};\n`;
  }

  const t = layout.match(/<([^>]+)>/)?.[1] ?? 'f32';
  const m = layout.match(/[0-9]/) ?? [2];

  const dims = +m[0];
  const dimsCast = dims === 1 ? t : `vec${dims}<${t}>`;

  const shaderType = (
    layout.match(/depth/) ? 'f32' :
    `vec4<${t}>`
  );

  const hasCast = needsCast(shaderType, type);

  return (
`@group(${set}) @binding(${binding}) var ${ns}${name}Texture: ${layout};
${sampler ? `@group(${set}) @binding(${binding + 1}) var ${ns}${name}Sampler: ${filter === 'comparison' ? 'sampler_comparison' : 'sampler'};\n` : ''}
fn ${ns}${name}(${args.map((t, i) => `${arg(i)}: ${t}`).join(', ')}) -> ${type} {
  ${absolute ?
    `let relUV = ${arg(0)} / ${dimsCast}(textureDimensions(${ns}${name}Texture));\n  ` : ``
  }${hasCast ? 'let v =' : 'return'} ${variant}(${ns}${name}Texture, ${sampler ? `${ns}${name}Sampler, ` : ''}${args.map((_, i) => `${i === 0 && absolute ? 'relUV' : arg(i)}`).join(', ')});
${hasCast ? '  return ' + makeSwizzle(shaderType, type, 'v') + ';\n' : ''
}}
`)
};

export const makeSamplerAccessor = (
  ns: string,
  set: number | string,
  binding: number,
  type: string,
  name: string,
) => {
  return (
    `@group(${set}) @binding(${binding}) var ${ns}${name}: ${type};
`)
};

export const makeVec3to4Accessor = (
  ns: string,
  type: string,
  format: string,
  name: string,
  accessor: string,
) => (
`fn ${ns}${name}(i: u32) -> ${type} {
  let i3 = i * 3u;

  let vx = ${ns}${accessor}(i3);
  let vy = ${ns}${accessor}(i3 + 1u);
  let vz = ${ns}${accessor}(i3 + 2u);

  let v = ${format}(vx, vy, vz);
  return ${needsCast(format, type) ? makeSwizzle(format, type, 'v') : 'v'};
}
`);

export const make8to32Accessor = (
  ns: string,
  type: string,
  format: string,
  name: string,
  accessor: string,
) => (
`fn ${ns}${name}(i: u32) -> ${type} {
  let b2 = i >> 2u;
  let f4 = i & 3u;

  let word = u32(${ns}${accessor}(b2));
  var v: ${format} = ${format}((word >> (f4 << 3u)) & 0xFFu);
  return ${needsCast(format, type) ? makeSwizzle(format, type, 'v') : 'v'};
}
`);

export const make16to32Accessor = (
  ns: string,
  type: string,
  format: string,
  name: string,
  accessor: string,
) => (
`fn ${ns}${name}(i: u32) -> ${type} {
  let b2 = i >> 1u;
  let f2 = i & 1u;

  let word = u32(${ns}${accessor}(b2));
  var v: ${format} = ${format}((word >> (f2 << 4u)) & 0xFFFFu);
  return ${needsCast(format, type) ? makeSwizzle(format, type, 'v') : 'v'};
}
`);

export const makeVec8to32Accessor = (
  ns: string,
  type: string,
  format: string,
  name: string,
  accessor: string,
) => {
  if (format.match(/^vec2/)) {
    return (
`fn ${ns}${name}(i: u32) -> ${type} {
  let i2 = i >> 1u;
  let f2 = i & 1u;
  let word = ${ns}${accessor}(i2);
  let short = word >> (f2 << 4u);
  let v = (vec2<u32>(short) >> vec2<u32>(0, 8)) & vec2<u32>(0xFF);
  return ${needsCast(format, type) ? makeSwizzle(format, type, 'v') : 'v'};
}
`);
  }
  else {
    return (
`fn ${ns}${name}(i: u32) -> ${type} {
  let word = ${ns}${accessor}(i);
  let v = (vec4<u32>(word) >> vec4<u32>(0, 8, 16, 24)) & vec4<u32>(0xFF);
  return ${needsCast(format, type) ? makeSwizzle(format, type, 'v') : 'v'};
}
`);
  }
}

export const makeVec16to32Accessor = (
  ns: string,
  type: string,
  format: string,
  name: string,
  accessor: string,
) => {
  if (format.match(/^vec2/)) {
    return (
`fn ${ns}${name}(i: u32) -> ${type} {
  let word = ${ns}${accessor}(i);
  let v = (vec2<u32>(word, word) >> vec2<u32>(0, 16)) & vec2<u32>(0xFFFF);
  return ${needsCast(format, type) ? makeSwizzle(format, type, 'v') : 'v'};
}
`);
  }
  else {
    return (
`fn ${ns}${name}(i: u32) -> ${type} {
  let i2 = i * 2;
  let word1 = ${ns}${accessor}(i2);
  let word2 = ${ns}${accessor}(i2 + 1);
  let v = (vec4<u32>(word1, word1, word2, word2) >> vec4<u32>(0, 16, 0, 16)) & vec4<u32>(0xFFFF);
  return ${needsCast(format, type) ? makeSwizzle(format, type, 'v') : 'v'};
}
`);
  }
};
